import { motion } from "framer-motion";
import { CheckCircle2, Lightbulb, TrendingUp, AlertTriangle, Target, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";

const LogoDesignGuide = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Ultimate Guide to Logo Design Services",
      "description": "Everything you need to know about professional logo design services, from fundamentals to advanced strategies. Learn how to create a logo that builds brand recognition and drives business growth.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/logo-design-services-guide"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Ultimate Guide to Logo Design Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-xl text-muted-foreground mb-8"
          >
            Everything you need to know about professional logo design services, from fundamentals to advanced strategies. Learn how to create a logo that builds brand recognition and drives business growth.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Your Logo Designed
          </motion.button>
        </div>
      </section>

      {/* What is Logo Design */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            What is Logo Design?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Logo design is the art and science of creating a visual symbol that represents your brand identity. A well-designed logo serves as the cornerstone of your brand, communicating your company's values, personality, and purpose in a single memorable image.
          </motion.p>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-lg text-muted-foreground"
          >
            Professional logo design goes beyond aesthetics—it's about creating a strategic asset that works across all touchpoints, from business cards to billboards, from websites to mobile apps. The best logos are simple, memorable, versatile, and appropriate for the brand they represent.
          </motion.p>
        </div>
      </section>

      {/* Why Professional Logo Design Matters */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Why Professional Logo Design Matters
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Target, title: "First Impressions", desc: "Your logo is often the first thing customers see. Make it count." },
              { icon: Lightbulb, title: "Brand Recognition", desc: "A memorable logo helps customers remember and recognize your brand." },
              { icon: TrendingUp, title: "Business Growth", desc: "Professional design builds trust and credibility with your audience." },
              { icon: CheckCircle2, title: "Competitive Advantage", desc: "Stand out from competitors with a unique, well-crafted logo." },
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <benefit.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Logos */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Types of Logos and When to Use Each
          </motion.h2>
          <div className="space-y-4 scroll-list">
            {[
              { type: "Wordmarks", desc: "Text-based logos focusing on typography. Best for: Companies with short, distinctive names." },
              { type: "Emblem Logos", desc: "Traditional badges or crests. Best for: Government, schools, organizations seeking authority." },
              { type: "Abstract Logos", desc: "Geometric shapes representing brand values. Best for: Tech companies, modern brands seeking uniqueness." },
              { type: "Mascot Logos", desc: "Character-based logos. Best for: Brands targeting families, sports teams, food companies." },
              { type: "Combination Logos", desc: "Text and symbol combined. Best for: Most businesses seeking versatility." },
              { type: "Minimalist Logos", desc: "Simple, clean designs. Best for: Modern brands seeking elegance and clarity." },
            ].map((logo, index) => (
              <motion.div
                key={logo.type}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{logo.type}</h3>
                <p className="text-muted-foreground">{logo.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Design Process */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            The Logo Design Process Explained
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Discovery & Research", desc: "Understanding your brand, target audience, competitors, and industry trends." },
              { step: 2, title: "Concept Development", desc: "Brainstorming and sketching multiple logo concepts based on research." },
              { step: 3, title: "Design & Refinement", desc: "Creating digital versions and refining the chosen concept." },
              { step: 4, title: "Testing & Feedback", desc: "Gathering feedback and testing the logo across different applications." },
              { step: 5, title: "Final Delivery", desc: "Providing all file formats and usage guidelines." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Design Pricing */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Logo Design Pricing Guide
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { level: "Budget", range: "$100-$500", features: ["Basic design", "1-2 concepts", "Limited revisions", "Basic formats"] },
              { level: "Professional", range: "$500-$2,000", features: ["Custom design", "3-5 concepts", "Multiple revisions", "All formats", "Brand guidelines"] },
              { level: "Premium", range: "$2,000-$10,000+", features: ["Full brand identity", "Unlimited concepts", "Unlimited revisions", "Complete system", "Strategy consultation"] },
            ].map((tier, index) => (
              <motion.div
                key={tier.level}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-xl mb-2">{tier.level}</h3>
                <div className="text-2xl font-bold text-coral mb-4">{tier.range}</div>
                <ul className="space-y-2">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-coral" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose a Logo Designer */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Choose a Logo Designer
          </motion.h2>
          <div className="space-y-4 scroll-list">
            {[
              { tip: "Review Portfolio", desc: "Look for diverse work and styles you admire." },
              { tip: "Check Experience", desc: "Years in business and industry expertise matter." },
              { tip: "Read Reviews", desc: "Client testimonials reveal working style and reliability." },
              { tip: "Compare Pricing", desc: "Ensure the price aligns with your budget and expectations." },
              { tip: "Assess Communication", desc: "Good designers listen and collaborate effectively." },
              { tip: "Verify Deliverables", desc: "Confirm you'll receive all necessary file formats." },
            ].map((item, index) => (
              <motion.div
                key={item.tip}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{item.tip}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Design Trends 2026 */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Logo Design Trends 2026
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { trend: "Minimalism", desc: "Clean, simple designs continue to dominate." },
              { trend: "Retro Revival", desc: "Vintage styles with modern twists are trending." },
              { trend: "Animated Logos", desc: "Motion graphics for digital platforms." },
              { trend: "Gradient Colors", desc: "Bold gradients add depth and modern appeal." },
              { trend: "Custom Typography", desc: "Unique lettering sets brands apart." },
              { trend: "Negative Space", desc: "Clever use of negative space creates memorable logos." },
            ].map((item, index) => (
              <motion.div
                key={item.trend}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2">{item.trend}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Common Logo Design Mistakes to Avoid
          </motion.h2>
          <div className="space-y-4 scroll-list">
            {[
              { mistake: "Overcomplicating the Design", desc: "Simple logos are more memorable and versatile." },
              { mistake: "Following Trends Blindly", desc: "Timeless design outperforms trendy fads." },
              { mistake: "Ignoring Scalability", desc: "Your logo must work at any size." },
              { mistake: "Poor Color Choices", desc: "Colors should align with brand psychology." },
              { mistake: "Using Stock Graphics", desc: "Original design prevents copyright issues." },
              { mistake: "Skipping Research", desc: "Understanding your audience is crucial." },
            ].map((item, index) => (
              <motion.div
                key={item.mistake}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <AlertTriangle className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.mistake}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Design for Different Industries */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Logo Design for Different Industries
          </motion.h2>
          <div className="space-y-4 scroll-list">
            {[
              { industry: "Technology", style: "Modern, minimalist, often abstract or geometric." },
              { industry: "Food & Beverage", style: "Warm colors, appetizing imagery, often playful." },
              { industry: "Finance", style: "Trustworthy colors (blue, green), conservative typography." },
              { industry: "Healthcare", style: "Clean, calming colors, simple and professional." },
              { industry: "Fashion", style: "Elegant, sophisticated, often minimalist with unique typography." },
              { industry: "Entertainment", style: "Bold, creative, often colorful and dynamic." },
            ].map((item, index) => (
              <motion.div
                key={item.industry}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{item.industry}</h3>
                <p className="text-muted-foreground">{item.style}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Measuring Success */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Measuring Logo Design Success
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { metric: "Brand Recognition", desc: "Survey customers to measure recall and recognition." },
              { metric: "Customer Feedback", desc: "Gather opinions on logo appeal and professionalism." },
              { metric: "Conversion Impact", desc: "Track if logo changes affect conversion rates." },
              { metric: "Social Engagement", desc: "Monitor logo performance in social media content." },
            ].map((item, index) => (
              <motion.div
                key={item.metric}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <Target className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{item.metric}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
              { title: "Types of Logos Explained", url: "/types-of-logos-explained", desc: "Explore different logo types and when to use them" },
              { title: "Minimalist Logo Design Guide", url: "/minimalist-logo-design-guide", desc: "Create powerful minimalist logos" },
              { title: "How to Design a Logo", url: "/how-to-design-a-logo", desc: "Step-by-step logo design process" },
              { title: "Logo Design Tools Comparison", url: "/logo-design-tools-comparison", desc: "Compare the best design software" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-3xl font-bold mb-4"
          >
            Ready to Create Your Perfect Logo?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-xl text-muted-foreground mb-8"
          >
            Let our expert designers create a logo that captures your brand essence and drives business growth.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Logo Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default LogoDesignGuide;
